// src/components/personal/sections/OrdersSection/components/OrderInfo/OrderPaymentsTab.tsx
import React from 'react';
import { Order, Payment } from '../../types/types';
import { TEXT_STYLES, COMPONENT_STYLES } from '@/theme';
import { TFunction } from 'i18next';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatDateTime } from '@/utils/dateUtils';
import { getPaymentStatusTextStatic, getPaymentStatusColor } from '../../utils/statusHelpers';

interface OrderPaymentsTabProps {
  order: Order;
  showAddPayment: boolean;
  newPayment: Partial<Payment>;
  disabled: boolean;
  isSubmitting: boolean;
  onToggleAddPayment: () => void;
  onInputChange: (field: keyof Payment, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onAddPayment: (e: React.FormEvent) => Promise<void>;
  t: TFunction;
  i18n: {
    language: string;
  };
}

const OrderPaymentsTab: React.FC<OrderPaymentsTabProps> = ({
  order,
  showAddPayment,
  newPayment,
  disabled,
  isSubmitting,
  onToggleAddPayment,
  onInputChange,
  onAddPayment,
  t,
  i18n
}) => {
  const { formatPrice } = useBusinessCurrency();
  
  // Безопасный доступ к payments
  const payments = order.payments || [];
  
  // Вычисляем сумму всех платежей
  const totalPaid = payments.reduce((sum, payment) => {
    const amount = typeof payment.amount === 'number' 
      ? payment.amount 
      : parseFloat(payment.amount || '0');
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);
  
  // Вычисляем оставшуюся сумму
  const totalAmount = order.cost_data?.total || 0;
  const remainingAmount = Math.max(0, totalAmount - totalPaid);

  return (
    <div className="space-y-4">
      {/* Кнопки действий */}
      <div className="flex justify-between items-center mb-2">
        <div className="text-sm">
          {payments.length > 0 && (
            <>
              <span className="mr-2">{t('payments.paid')}:</span>
              <span className="font-medium text-green-600">{formatPrice(totalPaid)}</span>
              
              {remainingAmount > 0 && (
                <>
                  <span className="mx-2">{t('payments.remaining')}:</span>
                  <span className="font-medium text-red-600">{formatPrice(remainingAmount)}</span>
                </>
              )}
            </>
          )}
        </div>
        
        <button
          onClick={onToggleAddPayment}
          className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary} px-3 py-1.5 text-sm`}
          disabled={disabled}
        >
          {showAddPayment ? t('common:cancel') : t('actions.add_payment')}
        </button>
      </div>

      {/* Форма добавления платежа */}
      {showAddPayment && (
        <div className="bg-background p-3 rounded-lg mb-4">
          <form onSubmit={onAddPayment} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
                  {t('payment.amount')}
                </label>
                <input
                  type="number"
                  value={newPayment.amount}
                  onChange={(e) => onInputChange('amount', e)}
                  required
                  className={COMPONENT_STYLES.input.base}
                  placeholder={remainingAmount > 0 
                    ? t('payment.remaining_amount_placeholder', { amount: formatPrice(remainingAmount) })
                    : t('payment.amount_placeholder')
                  }
                  disabled={disabled || isSubmitting}
                  onFocus={() => {
                    if (!newPayment.amount && remainingAmount > 0) {
                      onInputChange('amount', {
                        target: { value: remainingAmount.toString() }
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                />
              </div>
              
              <div>
                <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
                  {t('payment.method')}
                </label>
                <select
                  value={newPayment.method}
                  onChange={(e) => onInputChange('method', e)}
                  className={COMPONENT_STYLES.input.base}
                  disabled={disabled || isSubmitting}
                >
                  <option value="cash">{t('payment_method.cash')}</option>
                  <option value="card">{t('payment_method.card')}</option>
                  <option value="online">{t('payment_method.online')}</option>
                </select>
              </div>
              
              <div>
                <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
                  {t('payment.status')}
                </label>
                <select
                  value={newPayment.status}
                  onChange={(e) => onInputChange('status', e)}
                  className={COMPONENT_STYLES.input.base}
                  disabled={disabled || isSubmitting}
                >
                  <option value="pending">{t('payment_status.pending')}</option>
                  <option value="paid">{t('payment_status.paid')}</option>
                  <option value="error">{t('payment_status.error')}</option>
                  <option value="refund">{t('payment_status.refund')}</option>
                </select>
              </div>
              
              <div>
                <label className={`block ${TEXT_STYLES.small} text-text mb-1`}>
                  {t('payment.transaction_id')}
                </label>
                <input
                  type="text"
                  value={newPayment.transaction_id || ''}
                  onChange={(e) => onInputChange('transaction_id', e)}
                  className={COMPONENT_STYLES.input.base}
                  placeholder={t('payment.transaction_placeholder')}
                  disabled={disabled || isSubmitting}
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className={`${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary} px-3 py-1.5 text-sm`}
                disabled={disabled || isSubmitting || !newPayment.amount}
              >
                {isSubmitting ? t('common:saving') : t('common:save')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Список платежей */}
      {payments.length === 0 && !showAddPayment ? (
        <div className="text-center py-4 text-secondary">
          {t('order_details.no_payments')}
        </div>
      ) : (
        <div className="space-y-2">
          {payments.map((payment) => (
            <div 
              key={payment.id} 
              className="bg-background p-3 rounded-lg flex justify-between items-center"
            >
              <div>
                <div className={`${TEXT_STYLES.body} font-medium`}>
                  {formatPrice(payment.amount)}
                </div>
                <div className={TEXT_STYLES.small}>
                  {t(`payment_method.${payment.method}`)}
                </div>
              </div>
              
              <div className="text-right">
                <span className={`inline-block px-2 py-0.5 rounded-full ${TEXT_STYLES.tiny} ${getPaymentStatusColor(payment.status)}`}>
                  {getPaymentStatusTextStatic(payment.status, t)}
                </span>
                
                <div className={`${TEXT_STYLES.small} text-text mt-1`}>
                  {payment.payment_date 
                    ? formatDateTime(payment.payment_date, true, i18n.language)
                    : formatDateTime(payment.created_at, true, i18n.language)
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderPaymentsTab;